import { useMemo, useState } from "react";
import { BriefcaseIcon, UserIcon } from "lucide-react";

import type {
  EntityFragment,
  EntityMembersQuery,
  ExternalGrantsReceivedQuery,
  RoleFragment,
} from "@/__generated__/graphql";
import { SettingsViewCard } from "@/app/app/_/views/SettingsView/SettingsViewLayout";
import { Icon } from "@/components/atoms/Icon";
import { useRevokeExternalGrant } from "@/components/organisms/ExternalGrants/ExternalGrants.hooks";
import { useEntityRoles } from "@/components/organisms/Roles/Roles.hooks";
import { describePermissions } from "@/components/organisms/Roles/Roles.tools";
import { RolesSelect } from "@/components/organisms/Roles/RolesSelect";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useScope } from "@/hooks/useScope";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Checkbox } from "@/components/ui/checkbox";

type Received = NonNullable<
  ExternalGrantsReceivedQuery["externalGrantsReceived"]["data"]
>[number];
type Delegation = Received["delegations"][number];
type Member = Extract<
  NonNullable<EntityMembersQuery["node"]>,
  { __typename?: "EntityObject" }
>["accounts"][number];

/**
 * The books businesses share with a firm, for its owners to hand on to its
 * people: for each client - the person who shared them - each of their
 * businesses, and every member of the firm, ticked where they work in its
 * books, with the firm's role they do it as (never more than the business
 * allows). A firm hands on some of a client's businesses and not others by
 * ticking some. Its other members see who has what.
 */
export function EntityClientsPanel({ entity }: { entity: EntityFragment }) {
  const { accounts } = useScope();
  const isOwner = Boolean(
    accounts?.find(
      (account) => account?.entity?.identifier === entity.identifier,
    )?.isOwner,
  );
  const { data, loading } = useBackendQuery(
    Queries.EXTERNAL_GRANTS_RECEIVED_QUERY,
    {
      variables: { entityIdentifier: entity.identifier },
      fetchPolicy: "cache-and-network",
    },
  );
  const { data: membersData, loading: membersLoading } = useBackendQuery(
    Queries.ENTITY_MEMBERS_QUERY,
    { variables: { id: entity.id }, fetchPolicy: "cache-and-network" },
  );
  const { roles, isLoading: rolesLoading } = useEntityRoles(entity.id);
  const members: Member[] = useMemo(
    () =>
      membersData?.node?.__typename === "EntityObject"
        ? membersData.node.accounts.filter((member) => !member.isExternal)
        : [],
    [membersData],
  );
  const received = data?.externalGrantsReceived?.data ?? [];
  // A client is the person who shared: their businesses together, in the
  // order the server lists them.
  const clients = useMemo(() => {
    const byClient = new Map<string, Received[]>();
    for (const one of received) {
      const key = one.grant.sharedByIdentifier;
      byClient.set(key, [...(byClient.get(key) ?? []), one]);
    }
    return [...byClient.values()];
  }, [received]);
  const isLoading = (loading && !data) || (membersLoading && !membersData);

  if (!isLoading && received.length === 0) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <SettingsViewCard
          icon={BriefcaseIcon}
          title="Clients"
          description={`No one shares their books with ${entity.name} yet. When someone does, accept it for ${entity.name} from Invitations, then hand the books on to its people here.`}
          data-testid="entity-clients-empty"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {(isLoading ? [] : clients).map((books) => (
        <SettingsViewCard
          key={books[0].grant.sharedByIdentifier}
          icon={UserIcon}
          title={
            books[0].grant.sharedByName ?? books[0].grant.sharedByEmailAddress
          }
          description={`${books.length} ${books.length === 1 ? "business" : "businesses"} shared with ${entity.name}.`}
          data-testid={`entity-client-${books[0].grant.sharedByIdentifier}`}
        >
          {books.map((business) => (
            <EntityClientsGrant
              key={business.grant.identifier}
              received={business}
              firmName={entity.name}
              members={members}
              roles={roles}
              isOwner={isOwner}
              isLoading={rolesLoading}
            />
          ))}
        </SettingsViewCard>
      ))}
    </div>
  );
}

/**
 * One business's books shared with the firm: what the business allows it, the
 * way to give them back, and each member of the firm, ticked where they work
 * in them.
 */
function EntityClientsGrant({
  received,
  firmName,
  members,
  roles,
  isOwner,
  isLoading,
}: {
  received: Received;
  firmName: string;
  members: Member[];
  roles: RoleFragment[];
  isOwner: boolean;
  isLoading: boolean;
}) {
  const { grant, delegations } = received;
  const books = grant.isOwnBooks ? "Their own books" : grant.entityName;
  const { revoke, loading: isRevoking } = useRevokeExternalGrant();
  const refetch = {
    refetchQueries: ["ExternalGrantsReceived", "ExternalClients"],
  };
  const { mutate: delegate } = useBackendMutation({
    mutation: Mutations.DELEGATE_EXTERNAL_GRANT,
    ...refetch,
  });
  const { mutate: updateDelegation } = useBackendMutation({
    mutation: Mutations.UPDATE_EXTERNAL_GRANT_DELEGATION,
    ...refetch,
  });
  const { mutate: removeDelegation } = useBackendMutation({
    mutation: Mutations.REMOVE_EXTERNAL_GRANT_DELEGATION,
    ...refetch,
  });
  return (
    <div
      data-testid={`entity-client-grant-${grant.identifier}`}
      className="flex flex-col [border-top-width:1px] [border-top-style:solid] border-border"
    >
      <div className="flex items-center gap-3 pl-3 pr-3 min-h-11">
        <div className="flex text-muted-foreground/80">
          <Icon ReactIcon={BriefcaseIcon} size="md" />
        </div>
        <div className="flex-1 min-w-0 pt-1.5 pb-1.5">
          <Typography level="body-md" className="truncate">
            {books}
          </Typography>
          <Typography
            level="body-sm"
            className="truncate text-muted-foreground/80"
          >
            {`They allow ${firmName}: ${describePermissions(grant.rolePermissions).toLowerCase()}`}
          </Typography>
        </div>
        {isOwner ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              if (
                !window.confirm(
                  `Give ${grant.isOwnBooks ? `${grant.sharedByName ?? grant.sharedByEmailAddress}'s own books` : `${grant.entityName}'s books`} back? Everyone at ${firmName} stops working in them.`,
                )
              ) {
                return;
              }
              void revoke(grant.identifier);
            }}
            disabled={isRevoking}
            className="text-destructive hover:text-destructive"
          >
            {isRevoking && <Spinner />}Give back
          </Button>
        ) : null}
      </div>
      {members.map((member) => {
        const delegation =
          delegations.find(
            (one) => one.accountIdentifier === member.identifier,
          ) ?? null;
        return (
          <EntityClientsMemberRow
            key={member.identifier}
            grantIdentifier={grant.identifier}
            member={member}
            delegation={delegation}
            roles={roles}
            isOwner={isOwner}
            isLoading={isLoading}
            onTakeBack={() =>
              delegation &&
              void removeDelegation({
                variables: { input: { identifier: delegation.identifier } },
              })
            }
            onRole={(role) =>
              delegation
                ? void updateDelegation({
                    variables: {
                      input: {
                        identifier: delegation.identifier,
                        roleIdentifier: role.identifier,
                      },
                    },
                  })
                : void delegate({
                    variables: {
                      input: {
                        externalGrantIdentifier: grant.identifier,
                        accountIdentifier: member.identifier,
                        roleIdentifier: role.identifier,
                      },
                    },
                  })
            }
          />
        );
      })}
    </div>
  );
}

/**
 * One member of the firm in one business's books: ticked where they work in
 * them, with the role they do it as - and, while hovered, what the server
 * says that lets them do there, in plain words. Ticked, they are handed the
 * books once the role is picked: never guessed for them.
 */
function EntityClientsMemberRow({
  grantIdentifier,
  member,
  delegation,
  roles,
  isOwner,
  isLoading,
  onTakeBack,
  onRole,
}: {
  grantIdentifier: string;
  member: Member;
  delegation: Delegation | null;
  roles: RoleFragment[];
  isOwner: boolean;
  isLoading: boolean;
  onTakeBack: () => void;
  onRole: (role: RoleFragment) => void;
}) {
  const name = member.memberDisplayName ?? member.memberEmailAddress;
  const [isPicking, setIsPicking] = useState(false);
  const isTicked = delegation != null || isPicking;
  // None where the grant holds them no account in the business.
  const hint = delegation?.memberPermissions
    ? `${describePermissions(delegation.memberPermissions)}.`
    : isPicking && !delegation
      ? "Pick the role they work in these books as."
      : "Not working in these books.";

  return (
    <div
      data-testid={`entity-client-member-${grantIdentifier}-${member.identifier}`}
      className="flex items-center gap-3 pl-11 pr-3 min-h-11 [&:hover_[data-hint]]:opacity-[1] [&:focus-within_[data-hint]]:opacity-[1]"
    >
      <Checkbox
        checked={isTicked}
        disabled={!isOwner}
        onCheckedChange={() => {
          if (delegation) onTakeBack();
          setIsPicking(!isTicked);
        }}
        aria-label={`${name} works in these books`}
      />
      <div className="flex text-muted-foreground/80">
        <Icon ReactIcon={UserIcon} size="md" />
      </div>
      <Typography
        level="body-md"
        className={cn(
          "truncate",
          "shrink-0 max-w-[30%]",
          isTicked ? "text-foreground" : "text-muted-foreground/80",
        )}
      >
        {name}
      </Typography>
      <Typography
        level="body-sm"
        data-hint
        className="truncate flex-1 min-w-0 text-muted-foreground/80 opacity-[0]"
      >
        {hint}
      </Typography>
      {isTicked && (
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
        >
          <RolesSelect
            roles={roles}
            value={delegation?.roleIdentifier ?? null}
            disabled={!isOwner}
            onChange={onRole}
            data-testid={`entity-client-member-role-${grantIdentifier}-${member.identifier}`}
          />
        </Skeleton>
      )}
    </div>
  );
}
